import { useParams, useNavigate } from "react-router-dom";
import { getNonprofitById } from "../../../api/Nonprofits";
import { urlifyName } from "../../shared/Utils/PageUrl";
import { faArrowRight, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { MapPinLine, ArrowLeft } from "@phosphor-icons/react";
import { getPaginatedCampaigns } from "../../../api/Campaigns";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTiktok, faAmazon } from "@fortawesome/free-brands-svg-icons";
import { fetchBlogs } from "../../../api/FetchBlogs";
import chewyVector from "../../../assets/chewy_vector.svg";
import BlogCard from "../../Blog/BlogSearch/BlogCard/BlogCard";
import NotFound from "../../shared/Utils/NotFound";
import CampaignCard from "../../Campaigns/CampaignCard/CampaignCard";
import { useSelector } from "react-redux";
import DonationBoxv2 from "../../shared/DonationBox/DonationBoxv2";
import { extractMissionStatement } from "../../shared/Utils/ExtractMissionStatement";
import Spinner from "../../shared/LoadingSpinner/Spinner";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";

export default function NonprofitFeature() {
  const { org_name, nonprofit_id } = useParams(); // Access the id parameter using useParams hook
  const [nonprofit, setNonprofit] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localUserData, setLocalUserData] = useState(null);
  const [ifReadMore, setIfReadMore] = useState(false);
  const [npStatement, setNpStatement] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [ifCampaignExists, setIfCampaignExists] = useState(true);

  const userData = useSelector((state) => state.userData.userData);

  const navigate = useNavigate();

  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      setLocalUserData(JSON.parse(userData));
    }
  }, [userData]);

  const fetchCampaigns = async (page, search, species, age, nonprofitId) => {
    try {
      const response = await getPaginatedCampaigns(
        page,
        search,
        species,
        age,
        nonprofitId,
        ""
      );

      if (page === 1) {
        setCampaigns(response.results);
      } else {
        setCampaigns([...campaigns, ...response.results]);
      }

      if (!response.next) {
        setIfCampaignExists(false);
      } else {
        setIfCampaignExists(true);
      }
      return response;
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  useEffect(() => {
    const fetchNonprofit = async () => {
      const response = await getNonprofitById(parseInt(nonprofit_id));
      return response;
    };

    const fetchSuccessStories = async (paramNonprofit) => {
      if (!paramNonprofit) {
        return [];
      }
      const response = await fetchBlogs(paramNonprofit);
      return response.filter((blog) =>
        paramNonprofit.success_stories.includes(blog.id)
      );
    };
    const fetchAll = async () => {
      const tempNonprofit = await fetchNonprofit();
      const tempCampaigns = await fetchCampaigns(1, "", "", "", nonprofit_id);
      const tempSuccessStories = await fetchSuccessStories(tempNonprofit);

      const sentences = extractMissionStatement(
        tempNonprofit.mission_stmt,
        true
      );
      setNpStatement(sentences);

      setNonprofit(tempNonprofit);
      setCampaigns(tempCampaigns.results);
      setSuccessStories(tempSuccessStories);
      setLoading(false);
    };
    try {
      fetchAll();
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [nonprofit_id, org_name]);

  // Set user's active np sub to true if it exists
  useEffect(() => {
    let np_sub = [];
    if (localUserData && nonprofit) {
      if (
        localUserData.np_subscriptions &&
        localUserData.np_subscriptions.length > 0
      ) {
        np_sub = localUserData.np_subscriptions.filter(
          (sub) => nonprofit.org_name === sub.nonprofit && sub.active === true
        );
      }
    }
  }, [localUserData, nonprofit]);

  // scroll to top on load of this component:
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleClick = (item) => {
    navigate(
      `/campaigns/animal/${urlifyName(nonprofit.org_name)}/${urlifyName(
        item.animal_name
      )}/${item.id}`
    );
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  // Videos
  const handlePrevVideo = () => {
    setCurrentVideoIndex((prevIndex) =>
      prevIndex === 0
        ? nonprofit.video_urls.split(",").length - 1
        : prevIndex - 1
    );
  };

  const handleNextVideo = () => {
    setCurrentVideoIndex((prevIndex) =>
      prevIndex === nonprofit.video_urls.split(",").length - 1
        ? 0
        : prevIndex + 1
    );
  };

  const handleDotClick = (index) => {
    setCurrentVideoIndex(index);
  };

  if (loading) {
    return (
      <div className="flex flex-row mx-auto mt-20 sm:mt-40 md:mt-60 lg:mt-80 w-full justify-center">
        <Spinner />
      </div>
    );
  }

  if (error && !loading) {
    return <NotFound />;
  }

  // Handles switching from 'Read more' to 'Read less'
  const handleReadMoreButton = () => {
    return ifReadMore ? setIfReadMore(false) : setIfReadMore(true);
  };

  // Handles button to load more campaigns
  const handleLoadMoreCampaigns = () => {
    const response = fetchCampaigns(page + 1, "", "", "", nonprofit_id);
    setPage(page + 1);
  };

  if (!nonprofit && !loading) {
    return <NotFound />;
  }

  return (
    <div className="w-full sm:px-4 md:px-8 lg:px-20 px-4 xl:px-32 pt-20 sm:pt-32 md:pt-40 lg:pt-60 pb-16 sm:pb-24 md:pb-32">
      <div className="max-w-[1360px] mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-black hover:text-gray-600 mb-4"
        >
          <ArrowLeft size={20} className="w-5 h-5" />
          <span className="!text-base !font-medium">Back</span>
        </button>

        {nonprofit ? (
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_136px_1fr] gap-6 lg:gap-0">
            {/* Left Column - Nonprofit Information */}
            <div className="flex flex-col text-left">
              <h1 className="text-orange !font-poppins !text-3xl sm:!text-4xl md:!text-5xl !font-medium mb-4 text-left">
                {nonprofit.org_name}
              </h1>

              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <MapPinLine size={20} className="" />
                <span className="!font-montserrat !text-base sm:!text-lg">
                  {toTitleCase(nonprofit.address?.city)},{" "}
                  {nonprofit.address?.state}
                </span>
              </div>

              <div className="!text-sm sm:!text-base leading-relaxed mb-4 sm:mb-6 text-left">
                <div
                  dangerouslySetInnerHTML={{ __html: nonprofit.mission_stmt }}
                />
              </div>

              <div className="mb-4 sm:mb-6">
                <p className="text-sm sm:text-base font-medium text-left">
                  EIN: {nonprofit.ein_num}
                </p>
              </div>

              {/* Social Media Icons */}
              <div className="flex gap-4 mb-8 lg:mb-0">
                {nonprofit.instagram_url && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={nonprofit.instagram_url}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                      className="w-6 h-6 sm:w-7 sm:h-7"
                    >
                      <path
                        d="M16.5 8V8.01M4 8.5C4 7.43913 4.42143 6.42172 5.17157 5.67157C5.92172 4.92143 6.93913 4.5 8 4.5H16C17.0609 4.5 18.0783 4.92143 18.8284 5.67157C19.5786 6.42172 20 7.43913 20 8.5V16.5C20 17.5609 19.5786 18.5783 18.8284 19.3284C18.0783 20.0786 17.0609 20.5 16 20.5H8C6.93913 20.5 5.92172 20.0786 5.17157 19.3284C4.42143 18.5783 4 17.5609 4 16.5V8.5ZM9 12.5C9 13.2956 9.31607 14.0587 9.87868 14.6213C10.4413 15.1839 11.2044 15.5 12 15.5C12.7956 15.5 13.5587 15.1839 14.1213 14.6213C14.6839 14.0587 15 13.2956 15 12.5C15 11.7044 14.6839 10.9413 14.1213 10.3787C13.5587 9.81607 12.7956 9.5 12 9.5C11.2044 9.5 10.4413 9.81607 9.87868 10.3787C9.31607 10.9413 9 11.7044 9 12.5Z"
                        stroke="#1B3A5D"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                )}
                {nonprofit.facebook_url && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={nonprofit.facebook_url}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                      className="w-6 h-6 sm:w-7 sm:h-7"
                    >
                      <path
                        d="M7 10.5V14.5H10V21.5H14V14.5H17L18 10.5H14V8.5C14 8.23478 14.1054 7.98043 14.2929 7.79289C14.4804 7.60536 14.7348 7.5 15 7.5H18V3.5H15C13.6739 3.5 12.4021 4.02678 11.4645 4.96447C10.5268 5.90215 10 7.17392 10 8.5V10.5H7Z"
                        stroke="#1B3A5D"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                )}
                {nonprofit.website_url && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={nonprofit.website_url}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="25"
                      viewBox="0 0 25 25"
                      fill="none"
                      className="w-6 h-6 sm:w-7 sm:h-7"
                    >
                      <path
                        d="M12.5 2.34375C10.4913 2.34375 8.52768 2.9394 6.85749 4.05539C5.18731 5.17137 3.88556 6.75756 3.11685 8.61337C2.34815 10.4692 2.14702 12.5113 2.5389 14.4814C2.93079 16.4515 3.89807 18.2612 5.31845 19.6816C6.73883 21.1019 8.5485 22.0692 10.5186 22.4611C12.4887 22.853 14.5308 22.6519 16.3866 21.8832C18.2424 21.1144 19.8286 19.8127 20.9446 18.1425C22.0606 16.4723 22.6563 14.5087 22.6563 12.5C22.6532 9.80735 21.5821 7.22587 19.6781 5.32187C17.7741 3.41788 15.1927 2.34685 12.5 2.34375ZM21.0938 12.5C21.0944 13.2925 20.985 14.0813 20.7686 14.8438H17.0078C17.2474 13.2904 17.2474 11.7096 17.0078 10.1562H20.7686C20.985 10.9187 21.0944 11.7075 21.0938 12.5ZM9.96094 16.4062H15.0391C14.5388 18.0455 13.6703 19.5487 12.5 20.8008C11.3302 19.5483 10.4617 18.0453 9.96094 16.4062ZM9.58008 14.8438C9.31186 13.2928 9.31186 11.7072 9.58008 10.1562H15.4277C15.696 11.7072 15.696 13.2928 15.4277 14.8438H9.58008ZM3.90625 12.5C3.90557 11.7075 4.01502 10.9187 4.23145 10.1562H7.99219C7.75261 11.7096 7.75261 13.2904 7.99219 14.8438H4.23145C4.01502 14.0813 3.90557 13.2925 3.90625 12.5ZM15.0391 8.59375H9.96094C10.4612 6.9545 11.3297 5.45135 12.5 4.19922C13.6699 5.45168 14.5383 6.95472 15.0391 8.59375ZM20.1494 8.59375H16.6709C16.2325 6.98526 15.4936 5.47429 14.4932 4.14063C15.7019 4.43099 16.8335 4.97927 17.8105 5.74791C18.7874 6.51654 19.5867 7.48734 20.1533 8.59375H20.1494ZM10.5068 4.14063C9.50645 5.47429 8.76753 6.98526 8.32911 8.59375H4.84668C5.41336 7.48734 6.21257 6.51654 7.18954 5.74791C8.16651 4.97927 9.29814 4.43099 10.5068 4.14063ZM4.84668 16.4062H8.32911C8.76753 18.0147 9.50645 19.5257 10.5068 20.8594C9.29814 20.569 8.16651 20.0207 7.18954 19.2521C6.21257 18.4835 5.41336 17.5127 4.84668 16.4062ZM14.4932 20.8594C15.4936 19.5257 16.2325 18.0147 16.6709 16.4062H20.1533C19.5867 17.5127 18.7874 18.4835 17.8105 19.2521C16.8335 20.0207 15.7019 20.569 14.4932 20.8594Z"
                        fill="black"
                      />
                    </svg>
                  </a>
                )}
              </div>
            </div>

            {/* Gap Column */}
            <div className="hidden lg:block"></div>

            {/* Right Column - Donation Box */}
            <div className="flex justify-center lg:justify-start lg:-mt-16">
              <DonationBoxv2 nonprofit={nonprofit} />
            </div>
          </div>
        ) : (
          <div className="flex flex-row mx-auto mt-20 sm:mt-40 md:mt-60 lg:mt-80 w-full justify-center">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
}
